import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Calendar, Users, FileText, Check, CheckCircle, ExternalLink, X, Building, Upload, User, Briefcase, Download } from 'lucide-react';
import { toast } from 'sonner';

export default function PostularConvocatoria() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  const [convocatoria, setConvocatoria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [alreadyApplied, setAlreadyApplied] = useState(false);
  const [showExternalPopup, setShowExternalPopup] = useState(false);
  const [showFormModal, setShowFormModal] = useState(false);
  const [showRequisitosModal, setShowRequisitosModal] = useState(false);
  const [requisitosChecked, setRequisitosChecked] = useState({});
  const [formResponses, setFormResponses] = useState({});
  const [uploadingField, setUploadingField] = useState({}); // { [campoId]: bool }
  const [fileMeta, setFileMeta] = useState({}); // { [campoId]: { original_name } }
  const [formErrors, setFormErrors] = useState({});
  const [tipoPostulante, setTipoPostulante] = useState('persona');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchConvocatoria();
    checkIfAlreadyApplied();
  }, [id]);

  const fetchConvocatoria = async () => {
    try {
      const response = await fetch(`${API_URL}/api/convocatorias/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Convocatoria no encontrada');

      const data = await response.json();
      setConvocatoria(data);
      
      // Initialize form responses
      if (data.formulario_campos && data.formulario_campos.length > 0) {
        const initialResponses = {};
        data.formulario_campos.forEach(campo => {
          initialResponses[campo.id || campo.nombre] = campo.tipo === 'checkbox' ? false : '';
        });
        setFormResponses(initialResponses);
      }
    } catch (error) {
      toast.error('Error al cargar la convocatoria');
      navigate('/convocatorias-disponibles');
    } finally {
      setLoading(false);
    }
  };

  const checkIfAlreadyApplied = async () => {
    try {
      const response = await fetch(`${API_URL}/api/postulaciones/check/${id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAlreadyApplied(data.already_applied);
      }
    } catch (error) {
      console.error('Error checking application status');
    }
  };

  const validateForm = () => {
    const errors = {};
    if (convocatoria?.formulario_campos) {
      convocatoria.formulario_campos.forEach(campo => {
        const campoId = campo.id || campo.nombre;
        if (campo.requerido && !formResponses[campoId]) {
          errors[campoId] = `${campo.nombre} es requerido`;
        }
        if (campo.tipo === 'email' && formResponses[campoId]) {
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
          if (!emailRegex.test(formResponses[campoId])) {
            errors[campoId] = 'Email inválido';
          }
        }
      });
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInitiatePostulacion = async () => {
    if (alreadyApplied) {
      toast.error('Ya te has postulado a esta convocatoria');
      return;
    }

    // For "especial" convocatorias with previous requirements: show checklist first
    const requisitos = convocatoria?.requisitos_previos || [];
    if (
      convocatoria?.tipo_convocatoria === 'especial' &&
      requisitos.length > 0
    ) {
      setRequisitosChecked({});
      setShowRequisitosModal(true);
      return;
    }

    // If external, register postulation first, then show popup to open external link
    if (convocatoria?.convocatoria_externa && convocatoria?.enlace_externo) {
      await submitExternalPostulacion();
      return;
    }

    // If has custom form fields, show form modal
    if (convocatoria?.formulario_campos && convocatoria.formulario_campos.length > 0) {
      setShowFormModal(true);
      return;
    }

    // Otherwise submit directly
    await submitPostulacion({});
  };

  const proceedAfterRequisitos = async () => {
    setShowRequisitosModal(false);
    if (convocatoria?.convocatoria_externa && convocatoria?.enlace_externo) {
      await submitExternalPostulacion();
      return;
    }
    if (convocatoria?.formulario_campos && convocatoria.formulario_campos.length > 0) {
      setShowFormModal(true);
      return;
    }
    await submitPostulacion({});
  };

  // Submit postulacion for external convocatorias
  const submitExternalPostulacion = async () => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/postulaciones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          convocatoria_id: id,
          respuestas_formulario: {},
          tipo_postulante: tipoPostulante,
          es_externa: true
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al registrar postulación');
      }

      setAlreadyApplied(true);
      setShowExternalPopup(true);
      toast.success('Postulación registrada. Serás redirigido al formulario externo.');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      toast.error('Por favor complete todos los campos requeridos');
      return;
    }

    await submitPostulacion(formResponses);
  };

  const submitPostulacion = async (respuestas) => {
    try {
      setSubmitting(true);
      const response = await fetch(`${API_URL}/api/postulaciones`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          convocatoria_id: id,
          respuestas_formulario: respuestas,
          tipo_postulante: tipoPostulante
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al postularse');
      }

      toast.success('¡Te has postulado exitosamente!');
      setAlreadyApplied(true);
      setShowFormModal(false);
      navigate('/mis-postulaciones');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleInputChange = (campoId, value) => {
    setFormResponses(prev => ({ ...prev, [campoId]: value }));
    if (formErrors[campoId]) {
      setFormErrors(prev => ({ ...prev, [campoId]: null }));
    }
  };

  const openExternalLink = () => {
    if (convocatoria?.enlace_externo) {
      window.open(convocatoria.enlace_externo, '_blank', 'noopener,noreferrer');
    }
    setShowExternalPopup(false);
  };

  const openTermsInPopup = () => {
    if (convocatoria?.terminos_referencia_url) {
      let docUrl = convocatoria.terminos_referencia_url;
      if (docUrl.startsWith('/uploads/')) docUrl = '/api' + docUrl;
      const url = docUrl.startsWith('http') ? docUrl : `${API_URL}${docUrl}`;
      window.open(url, '_blank', 'noopener,noreferrer,width=900,height=700');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('es-CO', { day: '2-digit', month: 'long', year: 'numeric' });
  };

  const getEstadoBadge = (estado) => {
    const estados = {
      'Activa': 'bg-green-100 text-green-800',
      'Próxima': 'bg-blue-100 text-blue-800',
      'Cerrada': 'bg-red-100 text-red-800',
      'Pausada': 'bg-yellow-100 text-yellow-800'
    };
    return estados[estado] || 'bg-gray-100 text-gray-800';
  };

  const renderFormField = (campo) => {
    const campoId = campo.id || campo.nombre;
    const value = formResponses[campoId] || '';
    const error = formErrors[campoId];
    
    const baseInputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${error ? 'border-red-500' : 'border-slate-300'}`;

    switch (campo.tipo) {
      case 'texto':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            className={baseInputClass}
            placeholder={campo.placeholder || ''}
          />
        );
      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            rows={4}
            className={baseInputClass}
            placeholder={campo.placeholder || ''}
          />
        );
      case 'numero':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            className={baseInputClass}
          />
        );
      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            className={baseInputClass}
            placeholder="correo@ejemplo.com"
          />
        );
      case 'telefono':
        return (
          <input
            type="tel"
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            className={baseInputClass}
            placeholder="300 123 4567"
          />
        );
      case 'fecha':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            className={baseInputClass}
          />
        );
      case 'select':
        // Handle opciones as string (comma-separated) or array
        const opciones = Array.isArray(campo.opciones) 
          ? campo.opciones 
          : (campo.opciones ? campo.opciones.split(',').map(o => o.trim()).filter(o => o) : []);
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            className={baseInputClass}
          >
            <option value="">Seleccione...</option>
            {opciones.map((opcion) => (
              <option key={opcion} value={opcion}>{opcion}</option>
            ))}
          </select>
        );
      case 'checkbox':
        return (
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={value === true || value === 'true'}
              onChange={(e) => handleInputChange(campoId, e.target.checked)}
              className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-slate-700">{campo.descripcion || 'Sí, acepto'}</span>
          </label>
        );
      case 'archivo': {
        const isUploading = !!uploadingField[campoId];
        const uploadedUrl = typeof value === 'string' && value.length > 0 ? value : '';
        const originalName = fileMeta[campoId]?.original_name || uploadedUrl.split('/').pop() || '';
        const inputId = `campo-archivo-${campoId}`;
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-3">
              <input
                id={inputId}
                type="file"
                className="hidden"
                data-testid={`file-input-${campoId}`}
                accept=".pdf,.xls,.xlsx"
                onChange={async (e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const allowedExt = ['pdf', 'xls', 'xlsx'];
                  const ext = (file.name.split('.').pop() || '').toLowerCase();
                  if (!allowedExt.includes(ext)) {
                    toast.error('Solo se permiten archivos PDF o Excel (.pdf, .xls, .xlsx)');
                    e.target.value = '';
                    return;
                  }
                  if (file.size > 100 * 1024 * 1024) {
                    toast.error('El archivo no puede superar 100 MB');
                    e.target.value = '';
                    return;
                  }
                  try {
                    setUploadingField((prev) => ({ ...prev, [campoId]: true }));
                    const fd = new FormData();
                    fd.append('file', file);
                    const res = await fetch(`${API_URL}/api/upload/document`, {
                      method: 'POST',
                      headers: { Authorization: `Bearer ${token}` },
                      body: fd,
                    });
                    if (!res.ok) {
                      const err = await res.json().catch(() => ({}));
                      throw new Error(err.detail || 'Error al subir el archivo');
                    }
                    const data = await res.json();
                    handleInputChange(campoId, data.url);
                    setFileMeta((prev) => ({
                      ...prev,
                      [campoId]: { original_name: data.original_name || file.name },
                    }));
                    toast.success('Archivo cargado');
                  } catch (err) {
                    toast.error(err.message || 'Error al subir el archivo');
                  } finally {
                    setUploadingField((prev) => ({ ...prev, [campoId]: false }));
                    e.target.value = '';
                  }
                }}
              />
              <label
                htmlFor={inputId}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg border cursor-pointer text-sm font-medium ${
                  isUploading
                    ? 'bg-slate-100 border-slate-300 text-slate-500 cursor-wait'
                    : 'bg-white border-slate-300 text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`file-picker-${campoId}`}
              >
                <FileText className="h-4 w-4" />
                {isUploading
                  ? 'Subiendo...'
                  : uploadedUrl
                  ? 'Reemplazar archivo'
                  : 'Seleccionar archivo'}
              </label>
              {uploadedUrl && !isUploading && (
                <>
                  <a
                    href={uploadedUrl.startsWith('http') ? uploadedUrl : `${API_URL}${uploadedUrl}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1 truncate max-w-[200px]"
                    data-testid={`file-link-${campoId}`}
                  >
                    <CheckCircle className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span className="truncate">{originalName || 'Ver archivo'}</span>
                  </a>
                  <button
                    type="button"
                    onClick={() => {
                      handleInputChange(campoId, '');
                      setFileMeta((prev) => {
                        const copy = { ...prev };
                        delete copy[campoId];
                        return copy;
                      });
                    }}
                    className="text-xs text-red-600 hover:underline"
                    data-testid={`file-remove-${campoId}`}
                  >
                    Quitar
                  </button>
                </>
              )}
            </div>
            <p className="text-xs text-slate-400">
              Formatos permitidos: PDF, DOC/DOCX, XLS/XLSX, JPG, PNG · máx 100 MB
            </p>
          </div>
        );
      }
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(campoId, e.target.value)}
            className={baseInputClass}
          />
        );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-lg text-slate-500">Cargando convocatoria...</div>
      </div>
    );
  }

  if (!convocatoria) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center">
        <div className="text-lg text-red-500">Convocatoria no encontrada</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">Detalle de Convocatoria</h1>
            </div>
          </div>
          <button onClick={() => navigate('/convocatorias-disponibles')} className="flex items-center gap-2 text-slate-300 hover:text-white">
            <ArrowLeft className="h-5 w-5" />
            <span>Volver</span>
          </button>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden">
          
          {/* Image Banner */}
          {convocatoria.imagen_url && (
            <div className="w-full h-64 bg-slate-100 relative">
              <img 
                src={(() => {
                  let url = convocatoria.imagen_url;
                  if (url.startsWith('/uploads/')) url = '/api' + url;
                  return url.startsWith('http') ? url : `${API_URL}${url}`;
                })()}
                alt={convocatoria.nombre}
                className="w-full h-full object-cover"
              />
              <div className="absolute top-4 right-4">
                <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getEstadoBadge(convocatoria.estado)}`}>
                  {convocatoria.estado}
                </span>
              </div>
            </div>
          )}

          <div className="p-6 lg:p-8">
            {/* Title and Code */}
            <div className="mb-6">
              <span className="text-sm text-slate-500 font-mono">{convocatoria.codigo}</span>
              <h2 className="text-2xl font-bold text-slate-900 mt-1">{convocatoria.nombre}</h2>
            </div>

            {/* Key Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm font-medium">Fecha Inicio</span>
                </div>
                <p className="text-slate-900 font-semibold">{formatDate(convocatoria.fecha_inicio)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Calendar className="h-4 w-4" />
                  <span className="text-sm font-medium">Fecha Cierre</span>
                </div>
                <p className="text-slate-900 font-semibold">{formatDate(convocatoria.fecha_final)}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4">
                <div className="flex items-center gap-2 text-slate-600 mb-1">
                  <Building className="h-4 w-4" />
                  <span className="text-sm font-medium">Subgerencia</span>
                </div>
                <p className="text-slate-900 font-semibold text-sm">{convocatoria.subgerencia || convocatoria.area || '-'}</p>
              </div>
            </div>

            {/* Description */}
            <div className="mb-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Descripción</h3>
              <p className="text-slate-700 leading-relaxed whitespace-pre-wrap">{convocatoria.descripcion}</p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="bg-blue-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-blue-800 mb-1">Resolución</h4>
                <p className="text-blue-900">{convocatoria.resolucion || '-'}</p>
              </div>
              <div className="bg-purple-50 rounded-lg p-4">
                <h4 className="text-sm font-semibold text-purple-800 mb-1">Contrato</h4>
                <p className="text-purple-900">{convocatoria.contrato || '-'}</p>
              </div>
            </div>

            {/* Tipos de Beneficiario */}
            {convocatoria.tipos_beneficiario && convocatoria.tipos_beneficiario.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Tipos de Beneficiario
                </h3>
                <div className="flex flex-wrap gap-2">
                  {convocatoria.tipos_beneficiario.map((tipo) => (
                    <span key={tipo} className="px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm">
                      {tipo}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Custom Form Fields Preview */}
            {convocatoria.formulario_campos && convocatoria.formulario_campos.length > 0 && (
              <div className="mb-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <h3 className="text-md font-semibold text-yellow-800 mb-2">
                  📋 Formulario de Postulación
                </h3>
                <p className="text-sm text-yellow-700 mb-2">
                  Esta convocatoria requiere completar un formulario con {convocatoria.formulario_campos.length} campo(s):
                </p>
                <ul className="text-sm text-yellow-700 list-disc list-inside">
                  {convocatoria.formulario_campos.map((campo) => (
                    <li key={campo.id || campo.nombre}>
                      {campo.nombre} {campo.requerido && <span className="text-red-500">*</span>}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Terms and External Link */}
            <div className="mb-8">
              {/* Términos de Referencia - Archivos descargables */}
              {(convocatoria.terminos_referencia_archivos?.length > 0 || convocatoria.terminos_referencia_url) && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-4">
                  <h4 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <FileText className="h-5 w-5 text-blue-600" />
                    Términos de Referencia
                  </h4>
                  <div className="space-y-2">
                    {/* Multiple files */}
                    {convocatoria.terminos_referencia_archivos?.length > 0 ? (
                      convocatoria.terminos_referencia_archivos.map((archivo, idx) => {
                        // Extract just the filename from the URL (handle both full paths and filenames)
                        const urlFilename = archivo.url?.split('/').pop() || archivo.filename || '';
                        // Use download endpoint for forced download
                        const downloadUrl = `${API_URL}/api/download/document/${encodeURIComponent(urlFilename)}`;
                        const fileName = archivo.original_name || urlFilename || `Documento ${idx + 1}`;
                        const extension = fileName.split('.').pop()?.toUpperCase() || 'DOC';
                        
                        return (
                          <a
                            key={archivo.url || urlFilename || fileName}
                            href={downloadUrl}
                            className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors group"
                            data-testid={`download-terminos-${idx}`}
                          >
                            <div className={`p-2 rounded ${
                              extension === 'PDF' ? 'bg-red-100 text-red-600' :
                              extension === 'DOCX' || extension === 'DOC' ? 'bg-blue-100 text-blue-600' :
                              extension === 'XLSX' || extension === 'XLS' ? 'bg-green-100 text-green-600' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              <FileText className="h-5 w-5" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium text-slate-700 truncate group-hover:text-blue-700">
                                {fileName}
                              </p>
                              <p className="text-xs text-slate-500">{extension}</p>
                            </div>
                            <Download className="h-5 w-5 text-slate-400 group-hover:text-blue-600" />
                          </a>
                        );
                      })
                    ) : convocatoria.terminos_referencia_url && (
                      /* Legacy single file */
                      <a
                        href={`${API_URL}/api/download/document/${convocatoria.terminos_referencia_url.split('/').pop()}`}
                        className="flex items-center gap-3 p-3 bg-white border border-slate-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors group"
                        data-testid="download-terminos-legacy"
                      >
                        <div className="p-2 rounded bg-red-100 text-red-600">
                          <FileText className="h-5 w-5" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-700 truncate group-hover:text-blue-700">
                            Términos de Referencia
                          </p>
                          <p className="text-xs text-slate-500">PDF</p>
                        </div>
                        <Download className="h-5 w-5 text-slate-400 group-hover:text-blue-600" />
                      </a>
                    )}
                  </div>
                </div>
              )}

              {/* External Link Button */}
              {convocatoria.convocatoria_externa && convocatoria.enlace_externo && (
                <button
                  onClick={() => setShowExternalPopup(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                  data-testid="btn-enlace-externo"
                >
                  <ExternalLink className="h-5 w-5" />
                  Enlace del Formulario Externo
                </button>
              )}
            </div>

            {/* Additional Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 text-sm">
              {convocatoria.postular_una_vez && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                  <span className="text-yellow-800">⚠️ Solo puedes postularte una vez</span>
                </div>
              )}
              {convocatoria.postulacion_presencial && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                  <span className="text-orange-800">📍 Postulación presencial</span>
                </div>
              )}
              {convocatoria.hasta_agotar_recursos && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
                  <span className="text-red-800">💰 Hasta agotar recursos</span>
                </div>
              )}
              {convocatoria.tipo_convocatoria === 'privada' && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
                  <span className="text-purple-800">🔒 Convocatoria Privada</span>
                </div>
              )}
              {convocatoria.tipo_convocatoria === 'especial' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center" data-testid="badge-especial">
                  <span className="text-amber-800">⭐ Convocatoria Especial — con requisitos previos</span>
                </div>
              )}
            </div>

            {/* Tipo de Postulante Selection */}
            {!alreadyApplied && convocatoria.estado !== 'Cerrada' && (
              <div className="mb-6 p-4 bg-slate-50 rounded-lg">
                <label className="block text-sm font-semibold text-slate-700 mb-3">
                  ¿Cómo deseas postularte? <span className="text-red-500">*</span>
                </label>
                <div className="flex flex-wrap gap-4">
                  {convocatoria.permite_persona !== false && (
                    <label className={`flex items-center gap-3 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                      tipoPostulante === 'persona' 
                        ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-200' 
                        : 'bg-white border-slate-300 hover:border-slate-400'
                    }`}>
                      <input 
                        type="radio" 
                        name="tipoPostulante" 
                        value="persona"
                        checked={tipoPostulante === 'persona'}
                        onChange={(e) => setTipoPostulante(e.target.value)}
                        className="text-blue-600 w-5 h-5"
                      />
                      <User className={`h-5 w-5 ${tipoPostulante === 'persona' ? 'text-blue-600' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-medium ${tipoPostulante === 'persona' ? 'text-blue-700' : 'text-slate-700'}`}>Como Persona</p>
                        <p className="text-xs text-slate-500">Postulación individual</p>
                      </div>
                    </label>
                  )}
                  {convocatoria.permite_empresa !== false && (
                    <label className={`flex items-center gap-3 px-4 py-3 border-2 rounded-lg cursor-pointer transition-all ${
                      tipoPostulante === 'empresa' 
                        ? 'bg-orange-50 border-orange-500 ring-2 ring-orange-200' 
                        : 'bg-white border-slate-300 hover:border-slate-400'
                    }`}>
                      <input 
                        type="radio" 
                        name="tipoPostulante" 
                        value="empresa"
                        checked={tipoPostulante === 'empresa'}
                        onChange={(e) => setTipoPostulante(e.target.value)}
                        className="text-orange-600 w-5 h-5"
                      />
                      <Briefcase className={`h-5 w-5 ${tipoPostulante === 'empresa' ? 'text-orange-600' : 'text-slate-400'}`} />
                      <div>
                        <p className={`font-medium ${tipoPostulante === 'empresa' ? 'text-orange-700' : 'text-slate-700'}`}>Como Empresa</p>
                        <p className="text-xs text-slate-500">Postulación empresarial</p>
                      </div>
                    </label>
                  )}
                </div>
              </div>
            )}

            {/* Action Button */}
            <div className="border-t border-slate-200 pt-6">
              {alreadyApplied ? (
                <div className="flex items-center gap-3 p-4 bg-green-50 rounded-lg">
                  <Check className="h-6 w-6 text-green-600" />
                  <div>
                    <p className="font-semibold text-green-800">Ya te has postulado a esta convocatoria</p>
                    <p className="text-sm text-green-600">Puedes ver el estado de tu postulación en "Mis Postulaciones"</p>
                  </div>
                </div>
              ) : convocatoria.estado === 'Cerrada' ? (
                <div className="p-4 bg-red-50 rounded-lg text-center">
                  <p className="font-semibold text-red-800">Esta convocatoria ya está cerrada</p>
                </div>
              ) : (
                <button
                  onClick={handleInitiatePostulacion}
                  disabled={submitting}
                  className="w-full py-4 bg-[#0F172A] text-white font-semibold rounded-lg hover:bg-slate-800 disabled:opacity-50 transition-colors text-lg"
                  data-testid="btn-postularse"
                >
                  {submitting ? 'Procesando...' : 'POSTULARSE A ESTA CONVOCATORIA'}
                </button>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* External Link Popup */}
      {showExternalPopup && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-slate-900">Formulario Externo</h3>
              <button onClick={() => setShowExternalPopup(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="text-slate-600 mb-6">
              Esta convocatoria tiene un formulario de postulación externo. 
              Serás redirigido a una página externa para completar tu postulación.
            </p>
            <div className="bg-slate-50 p-3 rounded-lg mb-6 break-all">
              <p className="text-sm text-slate-500">Enlace:</p>
              <p className="text-blue-600 text-sm">{convocatoria?.enlace_externo}</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setShowExternalPopup(false)}
                className="flex-1 px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={openExternalLink}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2"
              >
                <ExternalLink className="h-4 w-4" />
                Abrir Formulario
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Requisitos Previos Modal (tipo "especial") */}
      {showRequisitosModal && convocatoria && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full my-8" data-testid="requisitos-modal">
            <div className="bg-slate-900 text-white rounded-t-xl p-5 flex items-start justify-between">
              <div>
                <h3 className="text-lg font-semibold leading-tight">{convocatoria.nombre}</h3>
                <p className="text-xs text-slate-300 mt-1">Requisitos básicos para la postulación</p>
              </div>
              <button
                onClick={() => setShowRequisitosModal(false)}
                className="rounded-full w-8 h-8 flex items-center justify-center bg-slate-700 hover:bg-slate-600"
                aria-label="Cerrar"
                data-testid="requisitos-close-btn"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="p-6">
              <p className="text-sm font-semibold text-slate-900 mb-4">
                Antes de preparar la postulación, debes confirmar que cumples los requisitos básicos:
              </p>

              {(() => {
                const reqs = convocatoria.requisitos_previos || [];
                const doneCount = reqs.filter((_, i) => requisitosChecked[i]).length;
                const pct = reqs.length > 0 ? Math.round((doneCount / reqs.length) * 100) : 0;
                return (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-xs mb-1.5">
                      <span className="font-medium text-slate-600">Progreso de requisitos</span>
                      <span className="font-semibold text-emerald-600" data-testid="requisitos-progress-text">
                        {doneCount} de {reqs.length} completados
                      </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-slate-200 overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 transition-all duration-300"
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })()}

              <h4 className="flex items-center gap-2 text-sm font-semibold text-slate-900 mb-3">
                <Check className="h-4 w-4" />
                Lista de verificación de requisitos
              </h4>

              <div className="space-y-2.5">
                {(convocatoria.requisitos_previos || []).map((req, idx) => {
                  const checked = !!requisitosChecked[idx];
                  return (
                    <label
                      key={idx}
                      className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-all ${
                        checked
                          ? 'bg-emerald-50 border-emerald-300'
                          : 'bg-white border-slate-200 hover:border-slate-300'
                      }`}
                      data-testid={`requisito-check-${idx}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) =>
                          setRequisitosChecked((prev) => ({ ...prev, [idx]: e.target.checked }))
                        }
                        className="mt-0.5 h-4 w-4 text-emerald-600 rounded"
                      />
                      <span className={`text-sm leading-relaxed ${checked ? 'text-emerald-900' : 'text-slate-700'}`}>
                        {req}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="p-6 pt-0">
              {(() => {
                const reqs = convocatoria.requisitos_previos || [];
                const doneCount = reqs.filter((_, i) => requisitosChecked[i]).length;
                const allDone = reqs.length > 0 && doneCount === reqs.length;
                return (
                  <button
                    onClick={proceedAfterRequisitos}
                    disabled={!allDone || submitting}
                    className={`w-full py-3 rounded-lg font-semibold text-sm transition-all ${
                      allDone
                        ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                        : 'bg-slate-200 text-slate-500 cursor-not-allowed'
                    }`}
                    data-testid="requisitos-continue-btn"
                  >
                    {allDone
                      ? 'Continuar al formulario de postulación'
                      : `Completa todos los requisitos para postularte (${doneCount}/${reqs.length})`}
                  </button>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Custom Form Modal */}
      {showFormModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full my-8">
            <div className="p-6 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white rounded-t-lg">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Formulario de Postulación</h3>
                <p className="text-sm text-slate-500">{convocatoria.nombre}</p>
              </div>
              <button onClick={() => setShowFormModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <form onSubmit={handleFormSubmit} className="p-6 space-y-6 max-h-[60vh] overflow-y-auto">
              {convocatoria.formulario_campos?.map((campo, index) => {
                const campoId = campo.id || campo.nombre;
                return (
                  <div key={campoId || `campo-${index}`}>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {campo.nombre}
                      {campo.requerido && <span className="text-red-500 ml-1">*</span>}
                    </label>
                    {campo.descripcion && campo.tipo !== 'checkbox' && (
                      <p className="text-xs text-slate-500 mb-2">{campo.descripcion}</p>
                    )}
                    {renderFormField(campo)}
                    {formErrors[campoId] && (
                      <p className="text-xs text-red-500 mt-1">{formErrors[campoId]}</p>
                    )}
                  </div>
                );
              })}
            </form>

            <div className="p-6 border-t border-slate-200 flex gap-3">
              <button
                type="button"
                onClick={() => setShowFormModal(false)}
                className="flex-1 px-4 py-3 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleFormSubmit}
                disabled={submitting}
                className="flex-1 px-4 py-3 bg-[#0F172A] text-white rounded-lg hover:bg-slate-800 disabled:opacity-50"
              >
                {submitting ? 'Enviando...' : 'Enviar Postulación'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
